import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  AppButton,
  Modal,
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/framework'
import {
  getAssignmentValidationError,
  getAssignmentValidationMessageParams,
} from '@/lib/timeline/assignmentValidation'
import { UNASSIGNED_ROW_ID } from '@/lib/timeline/constants'
import type { AssignableRequest, CallsignRow } from '@/types/timeline'

interface RequestManagementModalProps {
  isOpen: boolean
  onClose: () => void
  request: AssignableRequest | null
  callsigns: CallsignRow[]
  onAssign?: (requestId: string, callsignId: string) => void
  onUnassign?: (requestId: string) => void
  title?: string
  prompt?: string
  confirmLabel?: string
  initialCallsignId?: string
  isAssigned?: boolean
  isCallsignsLoading?: boolean
  hideUnassignConfirmCloseButton?: boolean
}

export const RequestManagementModal: React.FC<RequestManagementModalProps> = ({
  isOpen,
  onClose,
  request,
  callsigns,
  onAssign,
  onUnassign,
  title,
  prompt,
  confirmLabel,
  initialCallsignId,
  isAssigned = false,
  isCallsignsLoading = false,
  hideUnassignConfirmCloseButton = false,
}) => {
  const { t } = useTranslation()
  const [selectedCallsignId, setSelectedCallsignId] = useState<string>('')
  const [isUnassignConfirmOpen, setIsUnassignConfirmOpen] = useState(false)

  const currentCallsignId = useMemo(
    () =>
      callsigns.find((callsign) =>
        callsign.requests.some((callsignRequest) => callsignRequest.id === request?.id)
      )?.id,
    [callsigns, request?.id]
  )

  const assignableCallsigns = useMemo(
    () => callsigns.filter((callsign) => callsign.id !== UNASSIGNED_ROW_ID),
    [callsigns]
  )

  const assignmentOptions = useMemo<SearchableSelectOption[]>(() => {
    const callsignOptions = assignableCallsigns
      .filter((callsign) => Boolean(callsign.name.trim()))
      .map((callsign) => ({
        id: `callsign:${callsign.id}`,
        value: callsign.id,
        label: callsign.name,
        leadingLabel: callsign.staffName?.trim() ?? '',
        trailingLabel: callsign.name,
        groupLabel: t('requestAssign.callsignsGroup'),
      }))

    const staffOptions = assignableCallsigns
      .filter((callsign) => {
        const staffName = callsign.staffName?.trim()
        const callsignName = callsign.name.trim()
        return Boolean(staffName) && !callsignName
      })
      .map((callsign) => ({
        id: `staff:${callsign.id}`,
        value: callsign.id,
        label: callsign.staffName?.trim() ?? '',
        groupLabel: t('requestAssign.staffGroup'),
      }))

    return [...callsignOptions, ...staffOptions]
  }, [assignableCallsigns, t])

  const resolvedCallsignId =
    currentCallsignId && currentCallsignId !== UNASSIGNED_ROW_ID
      ? currentCallsignId
      : initialCallsignId && initialCallsignId !== UNASSIGNED_ROW_ID
        ? initialCallsignId
        : undefined

  const canUnassign = Boolean(onUnassign && (resolvedCallsignId || isAssigned))

  useEffect(() => {
    if (!isOpen) return
    setSelectedCallsignId(resolvedCallsignId ?? '')
  }, [isOpen, resolvedCallsignId])

  const handleCancel = () => {
    setSelectedCallsignId('')
    setIsUnassignConfirmOpen(false)
    onClose()
  }

  const handleAssign = () => {
    if (!request || !selectedCallsignId) return

    const targetCallsign = callsigns.find((callsign) => callsign.id === selectedCallsignId)
    if (targetCallsign) {
      const validationError = getAssignmentValidationError(request, targetCallsign)
      if (validationError) {
        toast.error(
          t(
            `assignmentValidation.${validationError}`,
            getAssignmentValidationMessageParams(request, targetCallsign)
          )
        )
        return
      }
    }

    if (onAssign) {
      onAssign(request.id, selectedCallsignId)
    }
    setSelectedCallsignId('')
    onClose()
  }

  const handleUnassignConfirm = () => {
    if (!request || !onUnassign) return
    onUnassign(request.id)
    setIsUnassignConfirmOpen(false)
    setSelectedCallsignId('')
    onClose()
  }

  if (!request) return null

  return (
    <>
      <Modal
        isOpen={isOpen && !(hideUnassignConfirmCloseButton && isUnassignConfirmOpen)}
        onClose={handleCancel}
        title={title ?? t('requestAssign.title')}
        size="md"
        showCloseButton={!(hideUnassignConfirmCloseButton && isUnassignConfirmOpen)}
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-700">{prompt ?? t('requestAssign.prompt')}</p>

          <div>
            <label
              htmlFor="assign-request-callsign"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t('requestAssign.selectCallsign')}
            </label>
            <SearchableSelect
              id="assign-request-callsign"
              selectedValue={selectedCallsignId}
              options={assignmentOptions}
              onChange={(option) => setSelectedCallsignId(option.value)}
              placeholder={
                isCallsignsLoading
                  ? t('common.loading')
                  : t('requestAssign.selectCallsignPlaceholder')
              }
              isLoading={isCallsignsLoading}
              emptyText={t('requestAssign.noOptions')}
              clearButtonAriaLabel={t('requestAssign.clearSelection')}
              onClear={() => setSelectedCallsignId('')}
            />
          </div>

          <div className="mt-4 flex items-center justify-between gap-gap-sm border-t border-border-lighter pt-4">
            <div>
              {canUnassign && (
                <AppButton
                  variant="danger"
                  size="md"
                  onClick={() => setIsUnassignConfirmOpen(true)}
                >
                  {t('requestAssign.unassign')}
                </AppButton>
              )}
            </div>
            <div className="flex items-center gap-gap-sm">
              <AppButton variant="secondary" size="md" onClick={handleCancel}>
                {t('requestAssign.cancel')}
              </AppButton>
              <AppButton
                variant="primary"
                size="md"
                onClick={handleAssign}
                disabled={
                  !selectedCallsignId ||
                  selectedCallsignId === resolvedCallsignId ||
                  isCallsignsLoading
                }
              >
                {confirmLabel ?? t('requestAssign.assign')}
              </AppButton>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isUnassignConfirmOpen}
        onClose={() => setIsUnassignConfirmOpen(false)}
        title={t('requestAssign.confirmUnassignTitle')}
        size="sm"
        showCloseButton={!hideUnassignConfirmCloseButton}
      >
        <div className="space-y-6">
          <p className="text-sm text-gray-700">{t('requestAssign.confirmUnassignMessage')}</p>
          <div className="mt-4 flex justify-end gap-gap-sm border-t border-border-lighter pt-4">
            <AppButton
              variant="secondary"
              size="md"
              onClick={() => setIsUnassignConfirmOpen(false)}
            >
              {t('requestAssign.cancel')}
            </AppButton>
            <AppButton variant="danger" size="md" onClick={handleUnassignConfirm}>
              {t('requestAssign.confirmUnassignAction')}
            </AppButton>
          </div>
        </div>
      </Modal>
    </>
  )
}
